import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmDeleteDialog from '../../../security/ConfirmDeleteDialog';
import Modal from '../Catalogos/EmpresasUnidadesOrg/EmpresasUnidadesOrgModalTab';
import { ENDPOINTS } from '../../Utils/Endpoints';
import { Models } from '../../Utils/ModelsGeneral';
import sigmaEdit from '../../../Pages/img/Editar.png';
import sigmaDelete from '../../../Pages/img/Borrar.png';
import AlertDialog from '../../../Lms/Components/Shared/AlertDialog';

const TITLETAB = 'Unidades Organizacionales';
const idKey = 'idEmpresasUnidadesOrg';


const endpoint = ENDPOINTS.empresasUnidadesOrg;
const endpointById = ENDPOINTS.empresasUnidadesOrgById;
const normalize = Models.empresasUnidadesOrg;

const endpointUnidades = ENDPOINTS.unidadesOrg;
const modeloUnidad = Models.unidadesOrg;

const PAGE = 5;

export default function EmpresasUnidadesOrgTab({ fkValue }) {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [editData, setEditData] = useState(null);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });

    const [page, setPage] = useState(1);
    const [editingRowId, setEditingRowId] = useState(null);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setFormOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const load = async () => {
        setLoading(true);
        try {
            const [resRel, resUni] = await Promise.all([
                axios.get(endpoint),
                axios.get(endpointUnidades),
            ]);

            let arr = (resRel?.data || []).map(normalize);
            const unidades = (resUni?.data || []).map(modeloUnidad);

            // Enriquecer
            arr = arr.map((rel) => {
                const uni = unidades.find(u => u.idUnidadOrganizacional === rel.idUnidadOrganizacional);
                const nombreUnidad = uni
                    ? uni.nombre
                    : `Unidad #${rel.idUnidadOrganizacional}`;

                return {
                    ...rel,
                    nombreUnidad,
                };
            });

            setRows(arr);
        } catch (e) {
            console.error(e);
            triggerAlert(`Ocurrió un error al cargar ${TITLETAB}. Inténtelo más tarde.`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (fkValue) load();
    }, [fkValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        let res = rows;
        // Filtrar por FK (Empresa)
        if (fkValue) {
            res = res.filter(r => Number(r.idEmpresa) === Number(fkValue));
        }
        // Filtrar por texto
        const q = query.trim().toLowerCase();
        if (q) {
            res = res.filter(r =>
                (r.nombreUnidad || '').toLowerCase().includes(q)
            );
        }
        return res;
    }, [rows, fkValue, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE;
        return filtered.slice(start, start + PAGE);
    }, [filtered, page]);

    const onNew = () => {
        setFormMode('new');
        setSelectedId(null);
        setEditData(null);
        setFormOpen(true);
        setEditingRowId(null);
    };

    const onEdit = (id) => {
        setFormMode('edit');
        setSelectedId(id);
        const row = rows.find(r => r[idKey] === id);
        setEditData(row);
        setFormOpen(true);
        setEditingRowId(id);
    };

    const onAskDelete = (row) => {
        setDeleteTarget({ id: row[idKey], name: row.nombreUnidad });
        setDeleteOpen(true);
    };

    const onConfirmDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await load();
        } catch (e) {
            console.error(e);
            triggerAlert('Ocurrió un error al eliminar. Inténtelo más tarde.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitFromModal = async (formData, mode) => {
        try {
            setLoading(true);
            const payload = {
                ...formData,
                idEmpresa: Number(fkValue),
            };

            let newId = null;
            if (mode === 'new') {
                const res = await axios.post(endpoint, payload);
                newId = res?.data?.[idKey] ?? (typeof res?.data === 'number' ? res.data : null);
            } else {
                await axios.put(endpoint, payload);
                newId = formData[idKey] || selectedId;
            }

            if (newId) setEditingRowId(newId);
            setFormOpen(false);
            await load();
        } catch (e) {
            console.error(e);
            const msg = e.response?.data?.message || 'Ocurrió un error al guardar. Inténtelo más tarde.';
            triggerAlert(msg);
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
            type="button"
        >
            {label}
        </button>
    );

    const pages = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    return (
        <div className="mini-crud">
            <div className="mini-topbar">
                <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input
                        className="roles-search"
                        placeholder="Buscar unidad..."
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    <button className="btn-action" onClick={onNew}>＋ Nuevo</button>
                </div>
            </div>

            <div className="roles-table-wrap">
                <div className="roles-table-scroller">
                    <table className="roles-table">
                        <thead>
                            <tr>
                                <th>Unidad Organizacional</th>
                                <th style={{ textAlign: 'center' }}>Editar</th>
                                <th style={{ textAlign: 'center' }}>Borrar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.map((row) => (
                                <tr key={row[idKey]} className={editingRowId === row[idKey] ? 'rk-row-active' : ''}>
                                    <td>{row.nombreUnidad}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        <button type="button" className="rk-iconbtn rk-iconbtn--edit" onClick={() => onEdit(row[idKey])}>
                                            <img src={sigmaEdit} alt="" className="rk-ico" />
                                        </button>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <button type="button" className="rk-iconbtn rk-iconbtn--del" onClick={() => onAskDelete(row)}>
                                            <img src={sigmaDelete} alt="" className="rk-ico" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!loading && paged.length === 0 && (
                                <tr><td colSpan={4}>Sin resultados.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="roles-pagination" style={{ marginTop: 8 }}>
                <div />
                <div className="roles-pagination-right">
                    <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
                    {pages.map((n) => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
                    <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
                </div>
            </div>

            {formOpen && (
                <Modal
                    open={formOpen}
                    mode={formMode}
                    data={editData}
                    fkValue={fkValue}
                    existingAssignments={rows}
                    onClose={() => setFormOpen(false)}
                    onSubmit={handleSubmitFromModal}
                />
            )}

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={onConfirmDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
